import type { FunctionComponent } from "react";
import { useRef, useState, useMemo, useEffect } from "react";

import { ChipDownContainer } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import {
    FooterButton,
    DropdownItem,
} from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import I18n from "pro-features/components/WithTranslation/src/I18n";

import { CategoriesListManager } from "./CategoriesListManager";
import type { ICategory, IMultiCategoriesChipDownProps } from "./MultiCategoriesChipdownTypes";
import { CategorySelectionState } from "./MultiCategoriesChipdownTypes";

const defaultInitialSelectedCategories: string[] = [];

export const MultiCategoriesChipDown: FunctionComponent<IMultiCategoriesChipDownProps> = ({
    initialSelectedCategories = defaultInitialSelectedCategories,
    categories,
    onDone,
    buttonText,
    searchPlaceholder,
    dropdownComponent,
    ignoreParentOnSelect = true,
    maxSelectedCategories,
    disableChildrenOnParentSelect = false,
}) => {
    const [categoriesListManager, setCategoriesListManager] = useState(
        new CategoriesListManager(categories, initialSelectedCategories),
    );
    const [selectedCategories, setSelectedCategories] = useState<ICategory[]>([]);
    // this state tracks the number of selected categories not including children of selected parents
    const [netSelectedCategoriesCount, setNetSelectedCategoriesCount] = useState<number | null>(null);

    const updateSelectedCategoriesAndCount = () => {
        const [initialSelectedCategories, initialNetSelectedCategoriesCount] =
            categoriesListManager.getSelectedCategories();
        setSelectedCategories(initialSelectedCategories);
        setNetSelectedCategoriesCount(initialNetSelectedCategoriesCount);
    };
    useEffect(() => {
        updateSelectedCategoriesAndCount();
    }, []);

    const [showApplyButton, setShowApplyButton] = useState(false);
    const selectedCategoriesTooltip = useMemo(() => {
        if (disableChildrenOnParentSelect) {
            return selectedCategories
                .filter((cat) => !cat.disabled)
                .map(({ name }) => name)
                .join(", ");
        }
        return selectedCategories
            .filter((cat) => !cat.sons || cat.sons.length === 0)
            .map(({ name }) => name)
            .join(", ");
    }, [selectedCategories]);

    const selectedCategoriesChipText = useMemo(() => {
        let selectedNonParentCategories = selectedCategories.filter((cat) => !cat.sons || cat.sons.length === 0);

        if (selectedNonParentCategories.length <= 0) {
            return null;
        }

        // We check if we have parent categories selected. in case a parent
        // category was selected, we want prioritize its name as the selected chip text
        // and add (+ num) next to its name as an indicator of the amount of child categories
        // selected. (this is a product decision)
        const selectedParentCategory = selectedCategories.find((cat) => cat.sons && cat.sons.length > 0);

        // If disabling children on parent select then when a parent is selected,
        // we count the whole "category" (parent and children) as one when listing
        // selected categories in the chip.
        if (disableChildrenOnParentSelect && selectedParentCategory) {
            selectedNonParentCategories = selectedCategories.filter(
                (cat) => (!cat.sons || cat.sons.length === 0) && !cat.isChild,
            );
            const suffix = selectedNonParentCategories.length ? ` + ${selectedNonParentCategories.length}` : "";
            return `${selectedParentCategory.name}${suffix}`;
        }

        if (selectedParentCategory) {
            return `${selectedParentCategory.name} + ${selectedNonParentCategories.length}`;
        }

        // otherwise - we take the name of the first selected child category as an arbritrary choice
        // and add + (num) in case more than one child category was selected (this is a product decision)
        const [firstSelectedChild, ...rest] = selectedNonParentCategories;
        return `${firstSelectedChild.name}${rest.length > 0 ? ` + ${rest.length}` : ``}`;
    }, [selectedCategories]);

    const selectedCategoryIds = useMemo(() => {
        return selectedCategories.reduce<Record<string, boolean>>((res, { id, selected }) => {
            if (selected === CategorySelectionState.FULL) {
                res[id] = true;
            }
            return res;
        }, {});
    }, [selectedCategories]);

    const flattenCategories = useMemo(() => {
        return categories.reduce<ICategory[]>((result, current) => {
            const { sons, ...rest } = current;
            result.push(rest);
            if (sons && sons.length > 0) {
                result.push(...sons);
            }
            return result;
        }, []);
    }, []);

    const onClick = (category: ICategory) => {
        const alreadySelected = selectedCategories.find((cat) => cat.id === category.id);
        if (maxSelectedCategories && netSelectedCategoriesCount === maxSelectedCategories && !alreadySelected) {
            return;
        }
        // sub-category
        if (category.parentId) {
            categoriesListManager.toggleSubCategory(category.id);
        }
        // parent category
        else {
            categoriesListManager.toggleParentCategory(category.id);
        }

        const [selected, selectedCategoriesCount] = categoriesListManager.getSelectedCategories();
        setSelectedCategories(selected);
        setNetSelectedCategoriesCount(selectedCategoriesCount);

        // display the "apply changes" button only in case we have some selected
        // categories.
        const hasSelectedCategories = selected && selected.length > 0;
        setShowApplyButton(hasSelectedCategories);
    };

    const onToggle = (isOpen: boolean) => {
        if (!isOpen) {
            // reset to initial selected categories
            categoriesListManager.reset();
            updateSelectedCategoriesAndCount();
            setShowApplyButton(false);
        }
    };

    const onClose = () => {
        categoriesListManager.clear();
        setSelectedCategories([]);
        onDone([]);
    };

    const onApply = () => {
        const selectedCategories = categoriesListManager.getSelectedCategories(true, ignoreParentOnSelect)[0];
        onDone(selectedCategories);
        setCategoriesListManager(
            new CategoriesListManager(
                categories,
                selectedCategories.map(({ id }) => id),
            ),
        );
        // @ts-ignore FIXME: strict migration - Property 'closePopup' does not exist on type 'never' (TS2339) — chipDownRef from useRef(undefined), ChipDownContainer ref API is untyped
        chipDownRef?.current?.closePopup();
    };
    const chipDownRef = useRef(undefined);

    const DropdownComponent = dropdownComponent || DropdownItem;

    // check if customCategory have segments:
    const hasSegments = (customCategory: ICategory) => {
        if ((CategoryServiceUtils.userCustomCategoriesObj[customCategory.id]?.segments?.length ?? 0) > 0) {
            return true;
        }
        return false;
    };

    return (
        <ChipDownContainer
            ref={chipDownRef}
            tooltipDisabled={selectedCategories.length === 1}
            // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to type 'string' (TS2322) — legacy: memo returns null when nothing is selected, ChipDownContainer expects string
            selectedText={selectedCategoriesChipText}
            tooltipText={selectedCategoriesTooltip}
            selectedIds={selectedCategoryIds}
            hasSearch={true}
            closeOnItemClick={false}
            onToggle={onToggle}
            onClick={onClick}
            onCloseItem={onClose}
            width={420}
            noResultsMessage={translate("dropdown.no.results")}
            buttonText={buttonText}
            searchPlaceHolder={searchPlaceholder}
            footerComponent={() =>
                showApplyButton && (
                    <FooterButton onClick={onApply}>
                        <I18n>common.apply</I18n>
                    </FooterButton>
                )
            }
            cssClassContainer="DropdownContent-container"
        >
            {flattenCategories.map((category) => {
                const parentCategory = selectedCategories.find(({ id }) => id === category.id);
                const isPartiallySelected =
                    parentCategory && parentCategory.selected === CategorySelectionState.PARTIAL;
                const hasSegmentsInCategory = hasSegments(category);
                // we disable selecting the dropdown items when either their parent is selected
                // or the max allowed items are selected ( assuming a max value and disabling of
                // children prop value was defined)
                const isDisabled =
                    (disableChildrenOnParentSelect &&
                        parentCategory &&
                        parentCategory.disabled &&
                        selectedCategories.find(({ id }) => id === category.parentId)?.selected ===
                            CategorySelectionState.FULL) ||
                    (maxSelectedCategories &&
                        netSelectedCategoriesCount === maxSelectedCategories &&
                        !parentCategory) ||
                    hasSegmentsInCategory;
                return (
                    <DropdownComponent
                        key={category.id}
                        id={category.id}
                        icon={category.icon}
                        name={category.name}
                        isChild={category.isChild}
                        parentId={category.parentId}
                        text={`${category.name} ${(category.count ?? 0) > 0 ? `(${category.count})` : ``}`}
                        partiallySelected={isPartiallySelected}
                        disabled={isDisabled}
                        tooltipText={
                            hasSegmentsInCategory
                                ? translate("digital.marketing.custom.industries.not.supported")
                                : null
                        }
                        className={`multi-categories-dropdown-component${category.isChild ? "--child" : "--parent"}`}
                    />
                );
            })}
        </ChipDownContainer>
    );
};
